import { useEffect, useMemo, useState } from "react";
import api from "../../api/client";
import { useAuth } from "../../auth/AuthContext";

const ALL_ROLES = ["admin", "owner", "operator"];
const SYSTEM_ORG_ID = 5;

const emptyForm = {
  id: null,
  company_id: "",
  full_name: "",
  user_email: "",
  user_role: "operator",
  password: "",
  is_active: true,
};

export default function Users() {
  const { user } = useAuth();
  const viewerOrgId = user?.company_id;
  const isAdmin = user?.user_role === "admin";

  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const params = !isAdmin && viewerOrgId ? { params: { company_id: viewerOrgId } } : undefined;
      const [u, o] = await Promise.all([api.get("/admin/users/", params), api.get("/admin/organizations/")]);
      setUsers(u.data);
      setOrgs(o.data);
    } catch (err) {
      setError("Failed to load users or organizations");
    }
  };

  useEffect(() => {
    if (user) {
      load();
    }
  }, [user]);

  const orgById = useMemo(() => {
    const map = new Map();
    orgs.forEach((o) => map.set(String(o.id), o));
    return map;
  }, [orgs]);

  const filteredUsers = useMemo(() => {
    const term = search.toLowerCase();
    const base = !isAdmin && viewerOrgId ? users.filter((u) => String(u.company_id) === String(viewerOrgId)) : users;
    return base
      .filter((u) => {
        const matchesStatus =
          statusFilter === "all" ||
          (statusFilter === "active" && u.is_active) ||
          (statusFilter === "inactive" && !u.is_active);
        const matchesSearch =
          !term ||
          (u.full_name || "").toLowerCase().includes(term) ||
          (u.user_email || "").toLowerCase().includes(term);
        return matchesStatus && matchesSearch;
      })
      .sort((a, b) =>
        (a.full_name || a.user_email || "").localeCompare(b.full_name || b.user_email || "")
      );
  }, [users, statusFilter, search, isAdmin, viewerOrgId]);

  const selectedOrg = orgById.get(String(form.company_id));
  const allowedRoles =
    selectedOrg?.id === SYSTEM_ORG_ID ? ALL_ROLES : selectedOrg?.org_role ? [selectedOrg.org_role] : ALL_ROLES;

  const openNew = () => {
    const defaultOrg = isAdmin ? "" : viewerOrgId || "";
    const defaultOrgObj = orgById.get(String(defaultOrg));
    const defaultRole =
      defaultOrgObj?.id === SYSTEM_ORG_ID
        ? "admin"
        : defaultOrgObj?.org_role
        ? defaultOrgObj.org_role
        : "operator";
    setForm({
      ...emptyForm,
      company_id: defaultOrg,
      user_role: defaultRole,
      password: "",
    });
    setError("");
    setModalOpen(true);
  };

  const openEdit = (u) => {
    setForm({
      id: u.id,
      company_id: u.company_id,
      full_name: u.full_name || "",
      user_email: u.user_email || "",
      user_role: u.user_role || "operator",
      password: "",
      is_active: Boolean(u.is_active),
    });
    setError("");
    setModalOpen(true);
  };

  const onFieldChange = (field) => (e) => {
    const value = e.target.value;
    if (field === "company_id") {
      const nextOrg = orgById.get(String(value));
      const nextRoles =
        nextOrg?.id === SYSTEM_ORG_ID ? ALL_ROLES : nextOrg?.org_role ? [nextOrg.org_role] : ALL_ROLES;
      setForm((f) => ({ ...f, company_id: value, user_role: nextRoles[0] || f.user_role }));
      return;
    }
    if (field === "is_active") {
      setForm((f) => ({ ...f, is_active: e.target.checked }));
      return;
    }
    setForm((f) => ({ ...f, [field]: value }));
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        company_id: Number(form.company_id),
        full_name: form.full_name,
        user_email: form.user_email,
        user_role: form.user_role,
        is_active: form.is_active,
      };

      if (form.id) {
        await api.put(`/admin/users/${form.id}`, payload);
        if (form.password.trim()) {
          await api.post(`/admin/users/${form.id}/reset-password`, { password: form.password });
        }
      } else {
        await api.post("/admin/users/", {
          ...payload,
          password: form.password,
        });
      }
      await load();
      setModalOpen(false);
      setForm(emptyForm);
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const availableOrgs = isAdmin ? orgs : orgs.filter((o) => String(o.id) === String(viewerOrgId));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Users</h1>
          <p className="text-sm text-slate-500">Manage users and their roles.</p>
        </div>
        <button
          onClick={openNew}
          className="px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-800 disabled:opacity-60"
          disabled={!isAdmin && !viewerOrgId}
        >
          Add user
        </button>
      </div>

      <div className="bg-white border rounded shadow-sm p-4 flex flex-col md:flex-row gap-3 md:items-center">
        <div className="flex-1">
          <label className="text-xs uppercase text-slate-500 font-semibold block">Search</label>
          <input
            className="mt-1 w-full rounded border px-3 py-2"
            placeholder="Search name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs uppercase text-slate-500 font-semibold block">Status</label>
          <select
            className="mt-1 rounded border px-3 py-2"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <div className="bg-white border rounded shadow-sm divide-y">
        {filteredUsers.length === 0 && (
          <div className="px-4 py-6 text-sm text-slate-500">No users match the filters.</div>
        )}
        {filteredUsers.map((u) => {
          const org = orgById.get(String(u.company_id));
          return (
            <button
              key={u.id}
              onClick={() => openEdit(u)}
              className="w-full text-left px-4 py-4 hover:bg-slate-50 flex items-center justify-between"
            >
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-900">{u.full_name}</span>
                  <span
                    className={`text-[11px] uppercase font-semibold px-2 py-0.5 rounded-full ${
                      u.user_role === "admin"
                        ? "bg-slate-800 text-white"
                        : u.user_role === "owner"
                        ? "bg-blue-50 text-blue-700"
                        : "bg-amber-50 text-amber-700"
                    }`}
                  >
                    {u.user_role}
                  </span>
                  <span
                    className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                      u.is_active ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {u.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
                <span className="text-xs text-slate-600">{u.user_email}</span>
                <span className="text-xs text-slate-500">
                  Org: {org?.company_name || "—"} (ID: {u.company_id})
                </span>
              </div>
              <span className="text-xs text-slate-400">ID: {u.id}</span>
            </button>
          );
        })}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow-xl w-full max-w-2xl border">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">{form.id ? "Edit user" : "Add user"}</h2>
              <button
                className="text-sm text-slate-500 hover:text-slate-800"
                onClick={() => {
                  setModalOpen(false);
                  setForm(emptyForm);
                }}
              >
                ✕
              </button>
            </div>
            <form onSubmit={onSave} className="p-4 space-y-3" autoComplete="off">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-slate-700">Organization</label>
                  <select
                    className="mt-1 w-full rounded border px-3 py-2"
                    value={form.company_id}
                    onChange={onFieldChange("company_id")}
                    required
                    disabled={!isAdmin}
                  >
                    <option value="">Select</option>
                    {availableOrgs.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.company_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-700">Role</label>
                  <select
                    className="mt-1 w-full rounded border px-3 py-2"
                    value={form.user_role}
                    onChange={onFieldChange("user_role")}
                  >
                    {allowedRoles.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-700">Full name</label>
                  <input
                    className="mt-1 w-full rounded border px-3 py-2"
                    value={form.full_name}
                    onChange={onFieldChange("full_name")}
                    required
                    autoComplete="new-name"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-700">Email</label>
                  <input
                    className="mt-1 w-full rounded border px-3 py-2"
                    value={form.user_email}
                    onChange={onFieldChange("user_email")}
                    required
                    autoComplete="new-email"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-700">
                    {form.id ? "Reset password (optional)" : "Password"}
                  </label>
                  <input
                    type="password"
                    className="mt-1 w-full rounded border px-3 py-2"
                    value={form.password}
                    onChange={onFieldChange("password")}
                    required={!form.id}
                    placeholder={form.id ? "Leave blank to keep current" : ""}
                    autoComplete={form.id ? "new-password" : "new-password"}
                  />
                </div>
                <div className="flex items-center gap-2 mt-1 md:mt-0">
                  <input
                    id="is_active"
                    type="checkbox"
                    className="h-4 w-4"
                    checked={form.is_active}
                    onChange={onFieldChange("is_active")}
                  />
                  <label htmlFor="is_active" className="text-sm text-slate-700">
                    Active
                  </label>
                </div>
              </div>

              {error && <div className="text-sm text-red-600">{error}</div>}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded border text-slate-700 hover:bg-slate-50"
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-800 disabled:opacity-60"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
